import {
  GUEST_QUOTE_LINK_PATH_PREFIX,
  guestContactMethodSchema,
  guestQuoteRequestResultSchema,
} from '@repo/api-contracts';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import type { GuestQuoteRequestReceipt } from '@/features/guest-quote-request';

/** 접수 결과를 싣는 history state 칸. */
const RECEIPT_STATE_KEY = 'guestQuoteReceipt';

/**
 * history state 는 브라우저가 들고 있는 값이라 그대로 믿지 않는다 — 접수 결과 칸(요청 id·접수번호·견적 링크 경로)과 연락 방법만
 * 받고(모르는 칸은 거부), 견적 링크는 `/q/{그 요청 id}/…` 모양일 때만 받는다. 다른 칸이 있는 state 는 그 칸만 본다.
 */
const receiptStateSchema = z.object({
  [RECEIPT_STATE_KEY]: z
    .strictObject({
      ...guestQuoteRequestResultSchema.shape,
      contactMethod: guestContactMethodSchema,
    })
    .refine((receipt) =>
      receipt.linkPath.startsWith(`${GUEST_QUOTE_LINK_PATH_PREFIX}/${receipt.quoteRequestId}/`),
    ),
});

const readReceipt = (state: unknown): GuestQuoteRequestReceipt | null => {
  const parsed = receiptStateSchema.safeParse(state);
  return parsed.success ? parsed.data[RECEIPT_STATE_KEY] : null;
};

interface GuestRequestReceipt {
  /** 이 화면에서 접수한 결과 — 방금 접수했거나, 같은 위치를 다시 열어(새로고침·뒤로 가기) history state 에 남아 있으면 값이 있다. */
  readonly receipt: GuestQuoteRequestReceipt | null;
  /** 접수 결과를 남긴다 — 바로 돌려주고, 같은 주소(경로·쿼리)를 바꿔치기로 다시 열어 history state 에도 싣는다. */
  readonly keepReceipt: (receipt: GuestQuoteRequestReceipt) => void;
}

/**
 * 비회원 요청 화면의 접수 결과(비회원 견적 설계 G12). 접수하면 임시 작업을 지우므로, 결과를 화면 상태에만 두면 새로고침·뒤로 가기로
 * 다시 열었을 때 "디자인을 찾지 못했어요"가 되고 접수번호·견적 링크를 다시 볼 곳이 없다(휴대폰 고객은 메일도 없다). 그래서 지금 기록
 * 항목의 history state 에 함께 둔다. 즐겨찾기·주소 복사로 연 새 항목에는 state 가 없다 — 완료 화면이 견적 링크 자체를 저장하라고 안내한다.
 */
export const useGuestRequestReceipt = (): GuestRequestReceipt => {
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  const [kept, setKept] = useState<GuestQuoteRequestReceipt | null>(null);

  const keepReceipt = (receipt: GuestQuoteRequestReceipt): void => {
    setKept(receipt);
    void navigate(`${pathname}${search}`, {
      replace: true,
      state: { [RECEIPT_STATE_KEY]: receipt },
    });
  };

  return { receipt: kept ?? readReceipt(state), keepReceipt };
};
